import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus } from 'lucide-react';

const deliverableStatuses = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' }
];

const invoiceStatuses = [
  { value: 'not_invoiced', label: 'Not Invoiced' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'cancelled', label: 'Cancelled' }
];

const paymentStatuses = [
  { value: 'pending', label: 'Pending' },
  { value: 'received', label: 'Received' },
  { value: 'partial', label: 'Partial' },
  { value: 'void', label: 'Void' }
];

export const FeeStructureFormFields = ({ form, setForm, getProjectValue, selectedProjectId }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm font-medium mb-2 block">Stage</label>
        <Input value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} placeholder="e.g., Design, Development" required />
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Deliverable</label>
        <Input value={form.deliverable} onChange={(e) => setForm({ ...form, deliverable: e.target.value })} placeholder="e.g., Wireframes" required />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm font-medium mb-2 block">% of Total Value</label>
        <Input type="number" step="0.01" value={form.percentage} onChange={(e) => {
          const pct = parseFloat(e.target.value) || 0;
          const projectValue = getProjectValue(selectedProjectId);
          setForm({ ...form, percentage: e.target.value, amount: ((pct / 100) * projectValue).toFixed(0) });
        }} required />
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Amount (INR)</label>
        <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
      </div>
    </div>
    <div>
      <label className="text-sm font-medium mb-2 block">Tentative Billing Date</label>
      <Input type="date" value={form.tentative_billing_date || ''} onChange={(e) => setForm({ ...form, tentative_billing_date: e.target.value })} />
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="text-sm font-medium mb-2 block">Deliverable Status</label>
        <Select value={form.deliverable_status} onValueChange={(v) => setForm({ ...form, deliverable_status: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{deliverableStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Invoice Status</label>
        <Select value={form.invoice_status} onValueChange={(v) => setForm({ ...form, invoice_status: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{invoiceStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Payment Status</label>
        <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{paymentStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  </>
);

export const AddFeeDialog = ({ open, onOpenChange, form, setForm, onSubmit, getProjectValue, selectedProjectId }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Add Fee Structure Item</DialogTitle></DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <FeeStructureFormFields form={form} setForm={setForm} getProjectValue={getProjectValue} selectedProjectId={selectedProjectId} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit">Add Item</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const EditFeeDialog = ({ open, onOpenChange, form, setForm, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Edit Fee Structure Item</DialogTitle></DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Stage</label>
            <Input value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} required />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Deliverable</label>
            <Input value={form.deliverable} onChange={(e) => setForm({ ...form, deliverable: e.target.value })} required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">% of Total Value</label>
            <Input type="number" step="0.01" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} required />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Amount (INR)</label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Tentative Billing Date</label>
          <Input type="date" value={form.tentative_billing_date || ''} onChange={(e) => setForm({ ...form, tentative_billing_date: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Deliverable Status</label>
            <Select value={form.deliverable_status} onValueChange={(v) => setForm({ ...form, deliverable_status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{deliverableStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Invoice Status</label>
            <Select value={form.invoice_status} onValueChange={(v) => setForm({ ...form, invoice_status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{invoiceStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Payment Status</label>
            <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{paymentStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const BulkEntryDialog = ({ open, onOpenChange, text, setText, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Bulk Add Deliverables</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Enter one item per line in format:<br />
          <code className="bg-muted px-1 rounded">Stage | Deliverable | Percentage | Billing Date (optional)</code>
        </p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Design | Concept Development | 10 | 2025-02-15&#10;Design | Schematic Design | 15 | 2025-03-01&#10;Development | Construction Docs | 25"
          rows={8}
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit}>Add Items</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
